import React, { useMemo, useState } from "react";
import { updateUserProfile } from "../../../graphql/client";
import "./UserInfoModal.css";

// Profile-completion modal. Rebuilt (2026-07) around two rules:
//   1. Never ask what we already know. Sex + shirt size usually come
//      from signup now, so most users only ever see the phone step.
//      Steps are computed from what's missing; "profile" mode shows
//      everything for editing.
//   2. Look like the signup flow (big pill buttons, progress dots) —
//      no antd widgets.
//
// Persistence: Submit saves through the authenticated
// updateUserProfile mutation BEFORE notifying the parent. The server
// enforces the answer-once sex lock; the UI mirrors it by never
// offering the sex choice once it's set.

const SHIRT_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];

const EVENT_TYPES = [
  "volleyball",
  "pickleball",
  "basketball",
  "dance",
  "yoga",
  "hiking",
  "biking",
  "skating",
  "running",
  "kayaking",
  "frisbee",
  "learning new languages",
  "learning new skills",
  "game nights",
  "beach hangouts",
  "paddle boarding",
];

const UserInfoModal = ({
  onClose,
  onSubmit,
  email,
  userInfo,
  cognitoToken,
  featureType,
}) => {
  const isEditMode = featureType === "profile";
  const sexLocked = userInfo?.sex === "male" || userInfo?.sex === "female";

  const [phone, setPhone] = useState(userInfo?.phone || "");
  const [sex, setSex] = useState(sexLocked ? userInfo.sex : "");
  const [shirtSize, setShirtSize] = useState(userInfo?.shirtSize || "M");
  const [interestedEvents, setInterestedEvents] = useState(
    userInfo?.interestedEvents || ""
  );
  const [referredBy, setReferredBy] = useState(userInfo?.referredBy || "");
  const [selectedEventTypes, setSelectedEventTypes] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const isPhoneReadOnly = isEditMode && !!userInfo?.phone;

  // Only ask what's missing. Profile mode shows every step so the
  // user can edit optional fields; gated flows get the shortest
  // possible path (usually just phone, since signup collects fit).
  const steps = useMemo(() => {
    const list = [];
    if (isEditMode || !userInfo?.phone) list.push("phone");
    if (isEditMode || !sexLocked || !userInfo?.shirtSize) list.push("fit");
    if (isEditMode) {
      list.push("interests");
      list.push("referred");
    }
    return list.length ? list : ["phone"];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  const validateStep = () => {
    if (step === "phone" && !phone.trim()) {
      return "Phone number is required.";
    }
    if (step === "fit" && !sexLocked && sex !== "male" && sex !== "female") {
      return "Please choose men's or women's fit.";
    }
    return "";
  };

  const handleNext = () => {
    const problem = validateStep();
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const handleBack = () => {
    setError("");
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const handleSubmit = async () => {
    const problem = validateStep();
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const selectedEvents = Object.keys(selectedEventTypes)
        .filter((key) => selectedEventTypes[key])
        .join(", ");
      const finalInterestedEvents = selectedEvents
        ? selectedEvents + (interestedEvents ? `, ${interestedEvents}` : "")
        : interestedEvents;

      const updatedProfile = await updateUserProfile({
        phone: phone.trim(),
        // Server ignores sex changes once locked; sending the locked
        // value back is a no-op.
        sex: sexLocked ? userInfo.sex : sex,
        shirtSize,
        interestedEvents: finalInterestedEvents,
        referredBy,
      });

      onSubmit({
        phone: updatedProfile.phone,
        sex: updatedProfile.sex,
        shirtSize: updatedProfile.shirtSize,
        interestedEvents: updatedProfile.interestedEvents,
        referredBy: updatedProfile.referredBy,
      });
    } catch (err) {
      console.error("Error submitting info:", err);
      setError("Failed to save information. Please try again.");
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const toggleEventType = (type) => {
    setSelectedEventTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const title =
    featureType === "chats"
      ? "Almost there"
      : featureType === "profile"
      ? "Edit your profile"
      : featureType === "subscribe"
      ? "Unlock membership options"
      : "One quick thing";

  const description =
    featureType === "profile"
      ? "Update your info below."
      : "We just need a little info to finish setting you up:";

  return (
    <div className="user-info-modal-overlay">
      <div className="user-info-modal uim">
        <div className="uim-header">
          <h3 className="uim-title">{title}</h3>
          <button className="uim-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="uim-body">
          <p className="uim-description">{description}</p>

          {steps.length > 1 && (
            <div className="uim-dots" aria-label={`Step ${stepIndex + 1} of ${steps.length}`}>
              {steps.map((s, i) => (
                <span
                  key={s}
                  className={
                    i === stepIndex
                      ? "uim-dot uim-dot--active"
                      : i < stepIndex
                      ? "uim-dot uim-dot--done"
                      : "uim-dot"
                  }
                />
              ))}
            </div>
          )}

          {step === "phone" && (
            <div className="uim-field">
              <label htmlFor="uim-phone">Phone number</label>
              {isPhoneReadOnly ? (
                <>
                  <input
                    id="uim-phone"
                    type="text"
                    value={phone}
                    readOnly
                    className="uim-input uim-input--readonly"
                  />
                  <div className="uim-hint">
                    Phone can't be changed after initial setup.
                  </div>
                </>
              ) : (
                <input
                  id="uim-phone"
                  type="tel"
                  className="uim-input"
                  placeholder="(904) 555-1234"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoFocus
                />
              )}
            </div>
          )}

          {step === "fit" && (
            <>
              {sexLocked ? (
                <div className="uim-field">
                  <label>Fit</label>
                  <div className="uim-locked-pill">
                    {userInfo.sex === "female" ? "Women's" : "Men's"}
                    <span className="uim-hint-inline">
                      (set at signup — ask an admin to change it)
                    </span>
                  </div>
                </div>
              ) : (
                <div className="uim-field">
                  <label>T-shirt fit</label>
                  <div className="uim-fit-buttons">
                    <button
                      type="button"
                      className={
                        sex === "female"
                          ? "uim-fit-btn uim-fit-btn--active"
                          : "uim-fit-btn"
                      }
                      onClick={() => setSex("female")}
                    >
                      Women's
                    </button>
                    <button
                      type="button"
                      className={
                        sex === "male"
                          ? "uim-fit-btn uim-fit-btn--active"
                          : "uim-fit-btn"
                      }
                      onClick={() => setSex("male")}
                    >
                      Men's
                    </button>
                  </div>
                </div>
              )}
              <div className="uim-field">
                <label htmlFor="uim-size">Shirt size</label>
                <select
                  id="uim-size"
                  className="uim-input"
                  value={shirtSize}
                  onChange={(e) => setShirtSize(e.target.value)}
                >
                  {SHIRT_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {step === "interests" && (
            <div className="uim-field">
              <label>What do you like to do?</label>
              <div className="uim-chips">
                {EVENT_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={
                      selectedEventTypes[type]
                        ? "uim-chip uim-chip--active"
                        : "uim-chip"
                    }
                    onClick={() => toggleEventType(type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
              <input
                type="text"
                className="uim-input"
                style={{ marginTop: 10 }}
                placeholder="Other interests, separated by commas"
                value={interestedEvents}
                onChange={(e) => setInterestedEvents(e.target.value)}
              />
            </div>
          )}

          {step === "referred" && (
            <div className="uim-field">
              <label htmlFor="uim-referred">Who referred you? (optional)</label>
              <input
                id="uim-referred"
                type="text"
                className="uim-input"
                placeholder="Friend's name, Instagram, Google…"
                value={referredBy}
                onChange={(e) => setReferredBy(e.target.value)}
              />
            </div>
          )}

          {error && <div className="uim-error">{error}</div>}
        </div>

        <div className="uim-footer">
          {stepIndex > 0 && (
            <button
              type="button"
              className="uim-btn"
              onClick={handleBack}
              disabled={loading}
            >
              Back
            </button>
          )}
          {isLastStep ? (
            <button
              type="button"
              className="uim-btn uim-btn--primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving…" : "Save"}
            </button>
          ) : (
            <button
              type="button"
              className="uim-btn uim-btn--primary"
              onClick={handleNext}
              disabled={loading}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInfoModal;
